import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { forEach } from 'lodash-es';
import { InboundFax } from 'app/core/models/inbound-fax.model';
import { LimitOffsetPaging, Sort } from 'app/core/models/paged-results/limit-offset-paging.model';
import { DownloadService } from 'app/core/services/download.service';
import { InboundFaxesService, PagedInboundFaxes } from 'app/core/services/inbound-faxes.service';
import { InboundFaxStatus } from '../../../core/enums/inbound-fax-status.enum';
import { FaxFilters, FaxQueueSettings } from '../../../core/models/user-settings/fax-settings.model';
import { UserSettings } from '../../../core/enums/user-settings.enum';
import { UserSettingsService } from '../../../core/services/user-settings.service';
import { UserTimingService } from '../../../core/services/user-timing.service';
import { UsersService } from 'app/core/services/users.service';
import { UserPresenceService } from 'app/core/services/user-presence.service';
import { UserRoleName } from 'app/core/enums/user-role-name.enum';
import { statusOptions } from 'app/core/options/inbound-fax-status.opts';
import { FormsModule } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { RouterLink } from '@angular/router';
import { DecimalPipe, DatePipe } from '@angular/common';
import { PaginationHeaderComponent } from '../../../shared/components/pagination-header/pagination-header.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { PhoneNumberComponent } from '../../../shared/components/phone-number/phone-number.component';
import { PatientNameComponent } from '../../../shared/components/patient-name/patient-name.component';
import { PrescriberNameComponent } from '../../../shared/components/prescriber-name/prescriber-name.component';
import { UserPresenceComponent } from '../../../shared/components/user-presence/user-presence.component';
import { PaginationComponent } from '../../../shared/components/pagination/pagination.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FaxStatusPipe } from '../../../shared/pipes/fax-status.pipe';
import { ClientSelectComponent } from '../../../shared/components/client-select/client-select.component';

@Component({
    templateUrl: './capture-admin-inbound-faxes.component.html',
    styleUrls: ['./capture-admin-inbound-faxes.component.scss'],
    imports: [FormsModule,
              NgSelectComponent,
              ClientSelectComponent,
              MatSlideToggle,
              RouterLink,
              PaginationHeaderComponent,
              FaIconComponent,
              PhoneNumberComponent,
              PatientNameComponent,
              PrescriberNameComponent,
              UserPresenceComponent,
              PaginationComponent,
              MatProgressSpinner,
              DecimalPipe,
              DatePipe,
              FaxStatusPipe]
})
export class CaptureAdminInboundFaxesComponent implements OnInit, OnDestroy {
  private inboundFaxesService = inject(InboundFaxesService);
  private downloadService = inject(DownloadService);
  private userSettingsService = inject(UserSettingsService);
  private userTimingService = inject(UserTimingService);
  private usersService = inject(UsersService);
  private userPresenceService = inject(UserPresenceService);

  inboundFaxes: InboundFax[] = [];
  paging: LimitOffsetPaging = LimitOffsetPaging.empty;
  filters: FaxFilters = new FaxFilters();
  sort: Sort = new Sort('received_at', 'desc');
  loading = true;

  faxStatuses = statusOptions;
  assignedToUsers = [];

  sortOptions = [
    { value: 'desc', display: 'Date Received: Newest' },
    { value: 'asc', display: 'Date Received: Oldest' },
  ];

  ngOnInit() {
    this.init();
    this.startTimeTracking();
  }

  ngOnDestroy(): void {
    this.userTimingService.stop();
    this.userPresenceService.unsubscribeAll();
  }

  onPageSizeChange(size) {
    this.paging.pageSize = size;
    this.getFaxes(this.paging.currentPage);
    this.saveQueueSettings();
  }

  onPageChange(page) {
    this.getFaxes(page);
    this.saveQueueSettings();
  }

  onResetFiltersClick() {
    this.filters = new FaxFilters();
    this.saveQueueSettings();
    this.getFaxes(1);
  }

  onFilterChange() {
    this.saveQueueSettings();
    this.getFaxes(1);
    this.startTimeTracking();
  }

  onSortChange() {
    this.saveQueueSettings();
    this.getFaxes(1);
  }

  download($event, inboundFax: InboundFax) {
    $event.preventDefault();
    this.downloadService.getDownloadUrl(inboundFax.fileExpirableDownloadPath).subscribe(expirableDownload => {
      this.downloadService.download(expirableDownload.url, null, '_blank');
    });
  }

  private init() {
    this.loadQueueSettings();
    this.loadAssignedUsers();
    this.getFaxes(1);
  }

  getFaxes(page) {
    this.loading = true;
    this.userPresenceService.unsubscribeAll();
    this.inboundFaxesService
      .getList(
        page,
        this.paging.pageSize,
        this.filters.selectedStatuses,
        this.filters.selectedClientIds,
        this.filters.selectedAssignedToIds,
        this.filters.includeArchived,
        this.sort
      )
      .subscribe((result: PagedInboundFaxes) => {
        this.inboundFaxes = result.inboundFaxes;
        this.paging = result.meta.paging;
        this.loading = false;
        this.subscribeToUserPresence();
      });
  }

  onViewClick(position: number) {
    this.saveQueueSettings(position);
  }

  private loadQueueSettings() {
    const queueSettings: FaxQueueSettings = this.userSettingsService.get<FaxQueueSettings>(
      UserSettings.captureAdminFaxQueueSettings
    );

    if (queueSettings) {
      this.filters = queueSettings.filters;
      this.paging = queueSettings.paging;
      if (queueSettings.sort) {
        this.sort = queueSettings.sort;
      }
    } else {
      this.filters.selectedStatuses = [InboundFaxStatus.fresh];
    }
  }

  private saveQueueSettings(position = null) {
    const queueSettings = new FaxQueueSettings();
    queueSettings.position = position;
    queueSettings.filters = this.filters;
    queueSettings.paging = this.paging;
    queueSettings.sort = this.sort;
    this.userSettingsService.save<FaxQueueSettings>(UserSettings.captureAdminFaxQueueSettings, queueSettings);
  }

  private loadAssignedUsers() {
    this.usersService.getByUserRole([UserRoleName.faxQueue]).subscribe((result) => {
      this.assignedToUsers = result.users;
    });
  }

  private startTimeTracking() {
    const data: any = { page: 'inbound_fax_queue' };
    data.statuses = this.filters.selectedStatuses;

    this.userTimingService.track(data);
  }

  private subscribeToUserPresence() {
    forEach(this.inboundFaxes, (inboundFax: InboundFax) => {
      this.userPresenceService.subscribe("fax", inboundFax.id)
    })
  }
}
